import React, { useEffect, useState } from 'react';
import {
  AspectRatio,
  Box,
  Center,
  Container,
  Flex,
  Img,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { GrNext, GrPrevious } from 'react-icons/gr';
import Slider from 'react-slick';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import ResourceLayout from '@/components/resources/ResourceLayout';
import SelectDropdown from '@/components/formComponents/Select';
import WinnerCard from '@/components/awards/WinnerCard';
import GallerySection from '@/components/committee/GallerySection';
import 'slick-carousel/slick/slick.css';
import { getContentList } from '@/lib/apiService';

const ReactPlayer = dynamic(() => import('react-player/youtube'), {
  ssr: false,
});

const PrevArrow = (props: any) => {
  const { style, onClick } = props;
  return (
    <Box
      position="relative"
      zIndex="100"
      left={{ xs: '-35px', sm: '-35px', md: '-50px', lg: '-60px', xl: '-65px' }}
      bottom={{ xs: '-220px', sm: '-230px', md: '-240px' }}
      style={{ ...style, display: 'block' }}
      boxSize={{ xs: '30px', sm: '30px', md: '35px', lg: '42px' }}
      border="2px solid"
      borderColor="text.mediumBlack"
      onClick={onClick}
      cursor="pointer"
      borderRadius="50%"
    >
      <Box
        mt={{ xs: '4px', md: '6px', lg: '9px' }}
        ml={{ xs: '1px', lg: '7px' }}
      >
        <GrPrevious size="20px" color="#56B6A5" />
      </Box>
    </Box>
  );
};

const NextArrow = (props: any) => {
  const { style, onClick } = props;
  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      position="relative"
      zIndex="100"
      right={{ xs: '-101.5%', sm: '-101%', md: '-102%', lg: '-853px' }}
      bottom={{ xs: '290px', sm: '270px' }}
      boxSize={{ xs: '30px', sm: '30px', md: '35px', lg: '42px' }}
      border="2px solid"
      borderColor="text.mediumBlack"
      style={{ ...style, display: 'block' }}
      onClick={onClick}
      cursor="pointer"
      borderRadius="50%"
    >
      <Box
        ml={{ xs: '6px', md: '8px', lg: '10px' }}
        mt={{ xs: '4px', md: '6px', lg: '8px' }}
      >
        <GrNext size="20px" />
      </Box>
    </Flex>
  );
};

const AwardsLayout = () => {
  const [year, setYear] = useState({ label: '2024', value: '2024' });
  const [nav1, setNav1] = useState<Slider | null>(null);
  const [nav2, setNav2] = useState<Slider | null>(null);
  const [awardsData, setAwardsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const settings = {
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  useEffect(() => {
    let isMounted = true;

    const fetchAwardsData = async () => {
      try {
        const res = await getContentList('awards_page', {
          limit: 10,
          where: {
            year: {
              in: [year.value],
            },
          },
        });
        if (isMounted) {
          setAwardsData(res.content.data);
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };

    fetchAwardsData();

    return () => {
      isMounted = false;
    };
  }, [year]);

  const handleChange = () => {};
  return (
    <Flex flexDir="column">
      <ResourceLayout
        heading="IVCA PE/VC Awards"
        onInputChange={handleChange}
      />
      <Flex
        py="20px"
        gap={{ xs: '20px', sm: '40px', md: '110px' }}
        justifyContent="center"
      >
        <Flex alignItems="center">
          <Text variant="dropdownSubLink" display={{ xs: 'none', sm: 'block' }}>
            Year
          </Text>
          <SelectDropdown
            name="Platform Dashboard Dropdown select"
            placeholder="2023"
            options={[
              { label: '2024', value: '2024' },
              { label: '2023', value: '2023' },
              { label: '2022', value: '2022' },
              { label: '2021', value: '2021' },
              { label: '2020', value: '2020' },
              { label: '2019', value: '2019' },
              { label: '2018', value: '2018' },
            ]}
            onChange={(e: any) => setYear(e)}
          />
        </Flex>
      </Flex>
      {loading ? (
        <Center h="200px">
          <Spinner color="primary.500" />
        </Center>
      ) : awardsData?.length === 0 ? (
        <Box w="100%" mb="30px" mx="auto">
          <Text
            variant="infoText"
            fontWeight="700"
            lineHeight="140%"
            textTransform="capitalize"
            textAlign="center"
          >
            No Awards found
          </Text>
        </Box>
      ) : (
        awardsData.map((awardsInfo: any) => (
          <>
            <Container key={awardsInfo.id} maxW="1200px">
              {awardsInfo.video_link && (
                <Flex pos="relative">
                  <Img
                    src="/png/leaf_left.png"
                    alt="Leaves design"
                    pos="absolute"
                    left={{ md: '-2%', lg: '1.2%', xl: '1.3%' }}
                    top="20%"
                    display={{ xs: 'none', md: 'block' }}
                  />
                  <Img
                    src="/png/leaf_right.png"
                    alt="Leaves design"
                    pos="absolute"
                    right={{ md: '-1.5%', lg: '1.8%', xl: '2.6%' }}
                    top="20%"
                    display={{ xs: 'none', md: 'block' }}
                  />
                  <AspectRatio
                    mx="auto"
                    ratio={16 / 9}
                    maxWidth="100%"
                    maxW="854px"
                    maxH="477px"
                    w={{ xs: '80%', md: '80%', lg: '80%', xl: '100%' }}
                    h="auto"
                    mb={{ xs: '30px', sm: '40px', md: '60px', lg: '76px' }}
                    overflow="hidden"
                  >
                    <ReactPlayer
                      url={awardsInfo.video_link}
                      controls
                      playing={false}
                      width="100%"
                      height="100%"
                    />
                  </AspectRatio>
                </Flex>
              )}
              {awardsInfo.winners[1].winner && (
                <Flex
                  justifyContent="center"
                  flexDir="column"
                  alignItems="center"
                >
                  <Text variant="headingText" fontSize="1.875rem">
                    Winners
                  </Text>
                  <Flex
                    justify="center"
                    pt="40px"
                    pb={{ xs: '30px', md: '40px', lg: '68px' }}
                    columnGap="20px"
                    rowGap="30px"
                    maxW="1090px"
                    flexWrap="wrap"
                  >
                    {awardsInfo.winners.map(
                      (winnerInfo: any, index: any) =>
                        winnerInfo.winner && (
                          <WinnerCard
                            title={winnerInfo.winner}
                            desc={winnerInfo.award_title}
                            key={index}
                            type="award"
                          />
                        )
                    )}
                  </Flex>
                </Flex>
              )}
              {awardsInfo.winners[0].awards_image && (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  flexDir="column"
                >
                  <Text
                    textAlign="center"
                    variant="headingText"
                    fontSize="24px"
                  >
                    Captures from Awards
                  </Text>

                  <Box
                    w={{ xs: '90%', sm: '90%', md: '90%', lg: '830px' }}
                    maxW="830px"
                    maxH="500px"
                    filter="drop-shadow(11.004px 11.004px 50.304px rgba(0, 0, 0, 0.2))"
                  >
                    <Slider
                      {...settings}
                      asNavFor={nav2 !== null ? nav2 : undefined}
                      ref={(slider1) => setNav1(slider1)}
                    >
                      {awardsInfo.winners.map((awardsImg: any) => (
                        <Box
                          w={{
                            xs: '90%',
                            sm: '90%',
                            md: '740px',
                            lg: '830px',
                          }}
                          minH="460px"
                          borderRadius="8px"
                        >
                          <Img
                            src={awardsImg.awards_image.location}
                            alt="awards image"
                            w="100%"
                            borderRadius="8px"
                            minH="460px"
                            maxH="460px"
                            objectFit="cover"
                          />
                        </Box>
                      ))}
                    </Slider>
                  </Box>
                  <Box
                    w={{ xs: '90%', sm: '90%', md: '90%', lg: '830px' }}
                    maxW="830px"
                    mt="20px"
                  >
                    {awardsInfo.winners[0].awards_image.location && (
                      <Slider
                        asNavFor={nav1 !== null ? nav1 : undefined}
                        ref={(slider2) => setNav2(slider2)}
                        slidesToShow={1}
                        dots={false}
                        arrows={false}
                      >
                        {awardsInfo.winners.map((awardsName: any) => (
                          <Flex w="100%" mb="50px">
                            <Text
                              variant="infoText"
                              fontSize="24px"
                              color="text.mediumBlack"
                              fontWeight="800"
                              letterSpacing="0.01em"
                              textAlign="center"
                            >
                              {awardsName.award_title}
                            </Text>
                          </Flex>
                        ))}
                      </Slider>
                    )}
                  </Box>
                </Flex>
              )}

              {awardsInfo.awards_booklet.location && (
                <Flex pt="80px" pb="20px" justifyContent="center" mt="30px">
                  <WinnerCard
                    desc="Awards Booklet"
                    title="The idea behind initiating the IVCA Awards has been to showcase the stakeholders and bring more community focus on the industry by recognizing its achievements."
                    type="booklet"
                    link={awardsInfo.awards_booklet.location}
                    year={awardsInfo.year}
                    imgSrc={
                      awardsInfo.year === '2022' ? '/png/award_booklet.png' : ''
                    }
                  />
                </Flex>
              )}
            </Container>
            {awardsInfo.jury.length !== 0 && (
              <Flex
                key={awardsInfo.id}
                pt={{
                  xs: '30px',
                  sm: '50px',
                  md: '70px',
                  lg: '100px',
                  xl: '142px',
                }}
                flexDir="column"
                alignItems="center"
              >
                <Text variant="headingText" fontSize="30px">
                  Our Best Awards Jury
                </Text>

                <GallerySection data={awardsInfo.jury} />
              </Flex>
            )}
            {awardsInfo.media_coverage_description && (
              <Flex
                key={awardsInfo.id}
                flexDir="column"
                alignItems="center"
                pt={{
                  xs: '20px',
                  sm: '30px',
                  md: '40px',
                  lg: '80px',
                  xl: '120px',
                }}
                pb={{
                  xs: '20px',
                  sm: '30px',
                  md: '40px',
                  lg: '80px',
                  xl: '114px',
                }}
                px="20px"
              >
                <Text variant="headingText" fontSize="30px" pb="20px">
                  Media Coverage
                </Text>
                <Text
                  maxW="936px"
                  variant="infoText"
                  fontWeight="600"
                  fontSize={{ xs: '14px', sm: '16px' }}
                  textAlign="center"
                  pb={{
                    xs: '20px',
                    sm: '30px',
                    md: '40px',
                    lg: '60px',
                    xl: '68px',
                  }}
                >
                  {awardsInfo.media_coverage_description}
                </Text>
                {awardsInfo.media_coverage[0].location && (
                  <Image
                    src={awardsInfo.media_coverage[0].location}
                    alt="media coverage"
                    width={800}
                    height={800}
                    objectFit="cover"
                  />
                )}
              </Flex>
            )}
          </>
        ))
      )}
    </Flex>
  );
};

export default AwardsLayout;
